import React, { useState, useEffect } from "react"
import WordmarkForm from "./WordmarkForm"
import FigurativeMarkForm from "./FigurativeMarkForm"
import SectionHeader from "../../ui/SectionHeader"
import { FiTag } from "react-icons/fi"
import { useResetListener } from "../../../hooks/useResetListener"

type MarkFormInput = {
  word?: string
  imageFile?: File | null
  text?: string
  colorClaim?: string
  category: string
  constitutionFile?: File | null
  euipoDocumentId?: string
  euipoConstitutionDocumentId?: string
  imageData?: string
  contentType?: string
}

export type MarkData = MarkFormInput & {
  type: "wordmark" | "figurativemark"
}

interface MarkSectionProps {
  onChange?: (data: MarkData | null) => void
  onTypeChange?: (type: "wordmark" | "figurativemark" | null) => void
  initialData?: MarkData | null
  disabled?: boolean
}

export default function MarkSection({
  onChange,
  onTypeChange,
  initialData,
  disabled = false
}: MarkSectionProps) {
  const [selectedType, setSelectedType] =
    useState<"wordmark" | "figurativemark" | null>(null)
  const [savedData, setSavedData] = useState<MarkData | null>(null)
  const [editData, setEditData] = useState<MarkFormInput | null>(null)
  const [originalData, setOriginalData] = useState<MarkData | null>(null)

  useEffect(() => {
    if (!initialData) return
    setSavedData(initialData)
    setOriginalData(initialData)
    setSelectedType(initialData.type)
    onTypeChange?.(initialData.type)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData])

  useResetListener(() => {
    if (disabled) return
    setSelectedType(null)
    setSavedData(null)
    setEditData(null)
    setOriginalData(null)
    onChange?.(null)
    onTypeChange?.(null)
  })

  const handleTypeSelect = (type: "wordmark" | "figurativemark") => {
    if (disabled) return
    if (savedData) return
    setSelectedType(type)
    setEditData(null)
    onChange?.(null)
    onTypeChange?.(type)
  }

  const handleSave = (data: MarkFormInput) => {
    if (!selectedType) return

    const fullData: MarkData = {
      type: selectedType,
      word: data.word ?? "",
      text: data.text ?? "",
      colorClaim: data.colorClaim ?? "",
      category: data.category,
      imageFile: data.imageFile ?? null,
      constitutionFile: data.constitutionFile ?? null,
      euipoDocumentId: data.euipoDocumentId,
      euipoConstitutionDocumentId: data.euipoConstitutionDocumentId,
      imageData: data.imageData ?? savedData?.imageData,
      contentType: data.contentType ?? savedData?.contentType
    }

    setSavedData(fullData)
    setEditData(null)
    onChange?.(fullData)
  }

  const handleEdit = () => {
    if (disabled) return
    if (!savedData) return
    setOriginalData(savedData)
    setEditData(savedData)
    setSavedData(null)
  }

  const handleCancelEdit = () => {
    if (disabled) return
    if (savedData) {
      setEditData(null)
    } else if (originalData) {
      setEditData(null)
      setSavedData(originalData)
      setOriginalData(null)
    } else {
      setSelectedType(null)
      setEditData(null)
    }
  }

  const handleDelete = () => {
    if (disabled) return
    setSavedData(null)
    setOriginalData(null)
    setSelectedType(null)
    onChange?.(null)
  }

  const getImageSrc = () => {
    if (savedData?.imageFile) {
      return URL.createObjectURL(savedData.imageFile)
    }
    if (savedData?.imageData) {
      return savedData.imageData
    }
    return null
  }

  const isReadOnly = disabled

  return (
    <section className="border-t border-gray-300 pt-4" id="trademark">
      <SectionHeader icon={<FiTag size={20} />} title="Trademark" />


      {!savedData && !isReadOnly && (
        <div className="flex gap-6 mb-6">
          <button
            type="button"
            onClick={() => handleTypeSelect("wordmark")}
            className={`flex-1 p-6 border rounded-lg text-center transition ${
              selectedType === "wordmark"
                ? "border-orange-500 bg-orange-100"
                : "border-gray-300 hover:border-orange-500"
            }`}
          >
            <img src="/wortmarke.svg" alt="" className="h-12 mx-auto mb-3" />
            <span className="font-medium">Word Mark</span>
          </button>

          <button
            type="button"
            onClick={() => handleTypeSelect("figurativemark")}
            className={`flex-1 p-6 border rounded-lg text-center transition ${
              selectedType === "figurativemark"
                ? "border-orange-500 bg-orange-100"
                : "border-gray-300 hover:border-orange-500"
            }`}
          >
            <img src="/bildmarke.svg" alt="" className="h-12 mx-auto mb-3" />
            <span className="font-medium">Figurative Mark</span>
          </button>
        </div>
      )}

      {selectedType === "wordmark" && !savedData && !isReadOnly && (
        <WordmarkForm
          onCancel={handleCancelEdit}
          onSave={handleSave}
          initialData={editData || undefined}
          disabled={disabled}
        />
      )}

      {selectedType === "figurativemark" && !savedData && !isReadOnly && (
        <FigurativeMarkForm
          onCancel={handleCancelEdit}
          onSave={handleSave}
          initialData={editData || undefined}
          disabled={disabled}
        />
      )}

      {savedData?.type === "wordmark" && (
        <div className="mt-4 max-w-sm">
          <div className="text-sm font-semibold text-blue-800 mb-2">
            Word Mark
          </div>

          <div className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white text-lg font-semibold text-gray-900">
            {savedData.word}
          </div>

          {!isReadOnly && (
            <div className="flex gap-4 mt-4 text-sm">
              <button onClick={handleEdit} className="text-blue-700 hover:underline">
                Edit
              </button>
              <button onClick={handleDelete} className="text-red-600 hover:underline">
                Delete
              </button>
            </div>
          )}
        </div>
      )}

      {savedData?.type === "figurativemark" && (
        <div className="mt-4 max-w-sm border rounded-lg bg-gray-50 px-5 py-4 shadow-sm">
          <div className="text-sm font-semibold text-blue-800 mb-2">
            Figurative Mark
          </div>

          {getImageSrc() && (
            <img
              src={getImageSrc()!}
              alt="Trademark"
              className="mx-auto h-48 object-contain mb-3"
            />
          )}

          {savedData.text && (
            <div className="text-sm font-medium text-gray-800 mb-1">
              {savedData.text}
            </div>
          )}

          {savedData.colorClaim && (
            <div className="text-sm text-gray-500">
              {savedData.colorClaim}
            </div>
          )}

          {!isReadOnly && (
            <div className="flex gap-4 mt-4 text-sm">
              <button onClick={handleEdit} className="text-blue-700 hover:underline">
                Edit
              </button>
              <button onClick={handleDelete} className="text-red-600 hover:underline">
                Delete
              </button>
            </div>
          )}
        </div>
      )}
    </section>
  )
}
